/* eslint-disable */
import moment from 'moment';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import styled, { createGlobalStyle } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { internalDateFormat } from '@hivebrite/design-system/Inputs';

import a11yDateFormat from 'hb-react/shared/utils/a11yDateFormat';

import './styles.scss';

const GlobalStyle = createGlobalStyle`
  .form-control {
    border: 1px solid ${(props) => props.theme.colors.dove};
  }

  .form-control, .flatpickr-input.flatpickr-mobile {
    border-radius: 4px;
    padding: 10px;
    color: ${(props) => props.theme.colors.asphalt};
    font: inherit;
    font-size: inherit;
    width: 100%;

    &:focus {
      border-color: ${(props) => props.theme.colors.primary};
    }
  }

  .flatpickr-day.selected {
    background-color: ${({ theme }) => `${theme.colors.primary} !important`};
    color: white !important;
  }
`;

const A11yDescription = styled.p`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

/**
 * @deprecated please use `@hivebrite/design-system/Inputs` instead
 */
const DateTimePicker = forwardRef(
  (
    {
      name,
      id,
      placeholder,
      value,
      defaultValue = '',
      className,
      disabled,
      displayFormat = 'Y/m/d',
      enableTime = false,
      locale = 'en-GB',
      maxDate,
      minDate,
      onChange,
      dataTestId,
      required,
      title,
      isValid,
    },
    inputRef,
  ) => {
    // Why? https://hivebrite.atlassian.net/browse/PM-12596
    const enUsRegex = /^(en|en-NA|deepbloo)$/;

    const options = useMemo(() => {
      const defaultOptions = {
        dateFormat: internalDateFormat.flatpickr,
        allowInput: true,
        altFormat: displayFormat,
        altInput: true,
        enableTime,
        maxDate,
        minDate,
        time_24hr: true,
      };

      if (locale.match(enUsRegex) && enableTime) {
        return {
          ...defaultOptions,
          altFormat: 'm/d/Y h:i K',
          enableTime: true,
          time_24hr: false,
        };
      }

      if (enableTime) {
        return {
          ...defaultOptions,
          altFormat: `${defaultOptions.altFormat} H:i`,
          enableTime: true,
        };
      }

      return defaultOptions;
    }, [displayFormat, enableTime, locale, maxDate, minDate]);

    const describedByID = useMemo(() => uuid(), []);

    const inputProps = useMemo(
      () => ({
        id,
        name,
        disabled,
        placeholder,
        'aria-required': required,
        'aria-invalid': isValid ? false : null,
        'aria-describedby': describedByID,
      }),
      [required, isValid, id],
    );

    const handleDateTimeChange = useCallback(
      (val) => {
        const finalValue = val[0]
          ? moment({
              year: val[0].getFullYear(),
              month: val[0].getMonth(),
              day: val[0].getDate(),
              hour: val[0].getHours(),
              minutes: val[0].getMinutes(),
            }).format(internalDateFormat.moment)
          : '';

        onChange?.(finalValue);
      },
      [onChange],
    );

    const [language, setLanguage] = useState(
      locale.match(enUsRegex) ? 'en' : locale,
    );
    const [loading, setLoading] = useState(true);

    const getLanguage = (locale) => {
      setLoading(true);
      if (locale.startsWith('en')) {
        setLanguage(null);
        setLoading(false);
      } else {
        import(`flatpickr/dist/l10n/${locale}.js`)
          .then((language) => setLanguage(language.default[locale]))
          .catch(() => setLanguage(null))
          .finally(() => setLoading(false));
      }
    };

    useEffect(() => {
      getLanguage(locale.match(enUsRegex) ? 'en' : locale);
    }, [locale]);

    return (
      <>
        <GlobalStyle />

        <Box data-testid={dataTestId} flexGrow={1}>
          {!loading && (
            <Flatpickr
              ref={inputRef}
              value={value === '' ? defaultValue : value}
              className={className}
              options={{ ...options, locale: language }}
              onChange={handleDateTimeChange}
              onCreate={(flatpickr) => {
                const { altInput, input } = flatpickr;
                const attributesToCopy = [
                  'aria-required',
                  'aria-invalid',
                  'aria-describedby',
                  'autocomplete',
                  'data-toggle',
                  'title',
                ];

                if (altInput) {
                  // Set ID to visible input instead of hidden one to associate with <label/>
                  if (input.id) {
                    altInput.id = input.id;
                    input.id = `${input.id}_hidden`;
                  }

                  attributesToCopy.forEach((attr) => {
                    const val = input.getAttribute(attr);

                    if (val != null) {
                      altInput.setAttribute(attr, input.getAttribute(attr));
                    }
                  });
                }
              }}
              onOpen={(selectedDates, dateStr, flatpickr) => {
                // Undo automatic select2 initialization whenever calendar opens.
                $(flatpickr.monthElements).each((_, el) => {
                  if ($(el).data('select2')) {
                    $(el).select2('destroy');
                  }
                });
              }}
              render={(props, ref) => (
                <>
                  <input
                    {...inputProps}
                    {...props}
                    data-toggle="tooltip"
                    ref={ref}
                    autoComplete="off"
                    onChange={handleDateTimeChange}
                    title={title}
                  />
                  <A11yDescription id={describedByID}>
                    {__(
                      'Enter the date with your keyboard in the following format: %{format}',
                    ).i__({
                      format: a11yDateFormat(options.altFormat),
                    })}
                  </A11yDescription>
                </>
              )}
            />
          )}
        </Box>
      </>
    );
  },
);

DateTimePicker.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  displayFormat: PropTypes.string,
  enableTime: PropTypes.bool,
  locale: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  required: PropTypes.bool,
  title: PropTypes.string,
  isValid: PropTypes.bool,
};

export default DateTimePicker;
